import { Button, DateFormField, Form, Typography } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import dayjs from "dayjs";
import { Mail, MessageSquare } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  FIELD_CONFIG,
  SIGNER_COLORS,
  type LayoutField,
} from "../SignatureRequestPdfLayoutEditor.constants";
import { formatContactsName, formatUsersName } from "@/lib/utils/format";
import { getSignatureRequestSignerId } from "../signatureRequestSigners";
import type { SignatureRequestSigner } from "../signatureRequestSigners";
import { ReviewRecipientCard } from "./ReviewRecipientCard";
import { SignatureRequestDocumentPreview } from "./SignatureRequestDocumentPreview";
import type { SignatureRequestPreviewDocument } from "./SignatureRequestDocumentPreview";

type ReviewAndSendStepProps = {
  matterId: string;
  previewDocument: SignatureRequestPreviewDocument | null;
  expirationDate: string;
  setExpirationDate: React.Dispatch<React.SetStateAction<string>>;
  expirationDateError?: string | null;
  clearExpirationDateError?: () => void;
  deliveryMethod: "email" | "text" | "both";
  setDeliveryMethod: (method: "email" | "text" | "both") => void;
  layoutFields: LayoutField[];
  signers: SignatureRequestSigner[];
  setSigners: React.Dispatch<React.SetStateAction<SignatureRequestSigner[]>>;
  canEditDocumentFiles: boolean;
  onMatterFileSaved?: (matterFileId: string) => Promise<void>;
  onFirmFileSaved?: (firmFileId: string) => Promise<void>;
  isRefreshingEditedDocument: boolean;
  documentRefreshError: boolean;
  onRetryDocumentRefresh: () => void;
};

type ReviewAndSendFormValues = {
  matterSignatureRequestExpirationDate: string;
};

const deliveryMethodCardClassName =
  "flex flex-1 flex-col items-center justify-center gap-1.5 rounded-xl border px-3 py-4 text-center transition-colors";

export function ReviewAndSendStep({
  matterId,
  previewDocument,
  expirationDate,
  setExpirationDate,
  expirationDateError,
  clearExpirationDateError,
  deliveryMethod,
  setDeliveryMethod,
  layoutFields,
  signers,
  setSigners,
  canEditDocumentFiles,
  onMatterFileSaved,
  onFirmFileSaved,
  isRefreshingEditedDocument,
  documentRefreshError,
  onRetryDocumentRefresh,
}: ReviewAndSendStepProps) {
  const form = useForm<ReviewAndSendFormValues>({
    values: { matterSignatureRequestExpirationDate: expirationDate },
  });
  const watchedExpirationDate = form.watch(
    "matterSignatureRequestExpirationDate",
  );

  useEffect(() => {
    setExpirationDate(watchedExpirationDate);
  }, [setExpirationDate, watchedExpirationDate]);

  return (
    <div className="bg-card grid h-full min-h-0 w-full overflow-hidden lg:grid-cols-[minmax(0,1fr)_400px]">
      <div className="min-h-0 min-w-0 bg-slate-50/40">
        <SignatureRequestDocumentPreview
          matterId={matterId}
          selectedDocument={previewDocument}
          canEditDocumentFiles={canEditDocumentFiles}
          editButtonLabel="Edit document"
          onMatterFileSaved={onMatterFileSaved}
          onFirmFileSaved={onFirmFileSaved}
          emptyTitle="No document selected"
          emptyDescription="Select a document to review it."
          renderPageOverlay={({ pageNumber }) => (
            <div className="pointer-events-none absolute inset-0">
              {layoutFields
                .filter((field) => field.page === pageNumber)
                .map((field) => {
                  const signerIndex = signers.findIndex(
                    (signer) =>
                      getSignatureRequestSignerId(signer) === field.signerId,
                  );
                  const signer = signers.find(
                    (item) =>
                      getSignatureRequestSignerId(item) === field.signerId,
                  );
                  const color =
                    SIGNER_COLORS[
                      (signer?.colorIndex ?? Math.max(0, signerIndex)) %
                        SIGNER_COLORS.length
                    ];
                  const signerName = signer
                    ? signer.kind === "contact"
                      ? formatContactsName(signer.contact)
                      : formatUsersName(signer.user)
                    : "";
                  const label = field.label || FIELD_CONFIG[field.type].label;

                  return (
                    <div
                      key={field.id}
                      aria-label={`${label}${signerName ? ` for ${signerName}` : ""}`}
                      className="absolute overflow-hidden rounded-[4px] border bg-white"
                      style={{
                        left: `${field.x * 100}%`,
                        top: `${field.y * 100}%`,
                        width: `${field.width * 100}%`,
                        height: `${field.height * 100}%`,
                        borderColor: color.border,
                      }}
                    >
                      <div
                        className={cn("h-full px-2 py-1", color.text)}
                        style={{ backgroundColor: color.bg }}
                      >
                        <span className="block truncate text-[11px] leading-tight font-semibold">
                          {label}
                        </span>
                        {signerName ? (
                          <span className="mt-0.5 block truncate text-[9px] leading-tight opacity-75">
                            {signerName}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        />
      </div>

      <aside className="border-border/80 bg-card min-h-0 overflow-y-auto border-l p-4">
        <Typography className="text-xl font-semibold">
          Review &amp; send
        </Typography>
        {isRefreshingEditedDocument ? (
          <p className="text-muted-foreground mt-3 text-sm" role="status">
            Loading your edited document…
          </p>
        ) : null}
        {documentRefreshError ? (
          <div className="mt-3 space-y-2" role="alert">
            <p className="text-destructive text-sm">
              The edited document could not be refreshed. Reload it before
              sending.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetryDocumentRefresh}
            >
              Retry
            </Button>
          </div>
        ) : null}

        <div className="mt-6 space-y-6">
          <div className="space-y-3">
            <Typography className="text-base font-semibold">
              Delivery method
            </Typography>
            <div className="flex gap-3">
              {(
                [
                  ["email", "Email", Mail],
                  ["text", "Text", MessageSquare],
                  ["both", "Both", null],
                ] as const
              ).map(([method, label, Icon]) => (
                <button
                  key={method}
                  type="button"
                  className={cn(
                    deliveryMethodCardClassName,
                    deliveryMethod === method
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-border/80 text-muted-foreground hover:border-primary/40 hover:bg-primary/5",
                  )}
                  onClick={() => {
                    setDeliveryMethod(method);
                  }}
                >
                  {Icon ? (
                    <Icon className="h-5 w-5" />
                  ) : (
                    <div className="flex items-center gap-1">
                      <Mail className="h-4.5 w-4.5" />
                      <MessageSquare className="h-4.5 w-4.5" />
                    </div>
                  )}
                  <span className="text-sm font-semibold">{label}</span>
                </button>
              ))}
            </div>
          </div>

          <Form {...form}>
            <DateFormField
              label="Expiration date*"
              minDate={dayjs().startOf("day").toDate()}
              suggestedDate={dayjs().add(1, "month").toDate()}
              suggestedDateText="Suggested (1 month)"
              register={form.register("matterSignatureRequestExpirationDate", {
                onChange: clearExpirationDateError,
              })}
              name="matterSignatureRequestExpirationDate"
            />
          </Form>
          {expirationDateError ? (
            <Typography
              className="text-destructive text-sm"
              data-testid="matterSignatureRequestExpirationDate-error"
            >
              {expirationDateError}
            </Typography>
          ) : null}

          <div>
            <Typography className="text-base font-semibold">
              Recipients
            </Typography>
            <div className="mt-3 space-y-2">
              {signers.map((signer, index) => (
                <ReviewRecipientCard
                  key={
                    signer.kind === "contact"
                      ? signer.contact.id
                      : signer.user.id
                  }
                  index={index}
                  colorIndex={signer.colorIndex}
                  hasFields={layoutFields.some(
                    (field) =>
                      field.signerId === getSignatureRequestSignerId(signer),
                  )}
                  signer={signer}
                  signers={signers}
                  setSigners={setSigners}
                />
              ))}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
